import { Box, Typography, LinearProgress, Chip, Paper, Skeleton, useTheme, Stack, Tooltip } from '@mui/material';
import { useGetBusinessBillingHistory } from '@/apis/business/setting/billing/hook';
import { IoIosInformationCircleOutline } from 'react-icons/io';

function UsageProgressCardSkeleton({ fullWidth }: { fullWidth?: boolean }) {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        bgcolor: theme.palette.primary.light,
        borderRadius: 2,
        px: 2,
        pt: 2,
        width: '100%',
        ...(fullWidth ? {} : { maxWidth: 400 })
      }}
    >
      {/* Header Skeleton */}
      <Box display='flex' justifyContent='space-between' alignItems='center' gap={1}>
        <Skeleton variant='text' width={120} height={20} />
        <Skeleton variant='rounded' width={60} height={24} />
      </Box>

      {/* Progress Bar Skeleton */}
      <Skeleton variant='rounded' height={10} sx={{ mt: 2, borderRadius: 5 }} />

      {/* Usage Stats Skeleton */}
      <Box display='flex' justifyContent='space-between' mt={1.5}>
        <Skeleton variant='text' width={80} height={16} />
        <Skeleton variant='text' width={100} height={16} />
      </Box>
    </Paper>
  );
}

type UsageTone = 'primary' | 'warning' | 'error';

function getUsageTone(percentage: number, isOverused: boolean): UsageTone {
  if (isOverused || percentage >= 90) return 'error';
  if (percentage < 70) return 'primary';
  return 'warning';
}

export default function UsageProgressCard({ fullWidth }: { fullWidth?: boolean }) {
  const { data: BillingData, isLoading } = useGetBusinessBillingHistory();
  const theme = useTheme();

  if (isLoading || !BillingData) {
    return <UsageProgressCardSkeleton fullWidth={fullWidth} />;
  }

  const used = BillingData?.per_min_usage?.used || 0;
  const remaining = BillingData?.per_min_usage?.remaining || 0;
  const percentage = BillingData?.per_min_usage?.progress_percentage || 0;
  const total = BillingData?.per_min_usage?.available_minutes || 0;
  const extraUsageSeconds = BillingData?.per_min_usage?.extra_usage || 0;
  const overusedMinutes = Math.round(extraUsageSeconds / 60);
  const isOverused = overusedMinutes > 0;
  const displayUsed = isOverused ? used + overusedMinutes : used;
  const tone = getUsageTone(percentage, isOverused);
  const accentColor = theme.palette[tone].main;
  const backgroundColor = theme.palette[tone].light;
  const leftLabel = isOverused ? `${overusedMinutes} overused` : `${remaining} minutes remaining`;

  return (
    <Paper
      elevation={0}
      sx={{
        bgcolor: backgroundColor,
        borderRadius: 2,
        px: '12px',
        pt: '12px',
        pb: '5px',
        width: '100%',
        ...(fullWidth ? {} : { maxWidth: 400 })
      }}
    >
      {/* Header */}
      <Box display='flex' justifyContent='space-between' alignItems='center' gap={1}>
        <Typography fontSize={14} fontWeight={400} noWrap>
          Minutes Usage
        </Typography>
        <Chip
          label={`${percentage ? Math.round(percentage) : 0}% used`}
          variant='outlined'
          sx={{
            borderColor: accentColor,
            borderRadius: 4,
            color: accentColor,
            fontWeight: 500,
            fontSize: '0.65rem',
            paddingX: '8px',
            height: 24,
            bgcolor: 'transparent'
          }}
        />
      </Box>

      {/* Progress Bar */}
      <LinearProgress
        variant='determinate'
        value={percentage || 0}
        sx={{
          mt: 1.5,
          height: '8px',
          borderRadius: 5,
          backgroundColor: '#fff',
          '& .MuiLinearProgress-bar': {
            backgroundColor: accentColor,
            borderRadius: 5
          }
        }}
      />

      {/* Usage Stats */}
      <Box display='flex' justifyContent='space-between' mt={0.5}>
        <Typography fontSize={12} color='text.primary'>
          {leftLabel}
        </Typography>
        <Stack direction='row' alignItems='center' gap={0.5}>
          <Typography fontSize={12} color='text.primary'>
            {displayUsed}/{total}
          </Typography>
          <Tooltip
            title={<TooltipComponent perMinuteCost={BillingData?.per_min_usage?.unit_amount} />}
            placement='top'
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: 'black',
                  color: 'white',
                  fontSize: '12px',
                  borderRadius: 1,
                  p: 1
                }
              }
            }}
          >
            <Box mt={0.4}>
              <IoIosInformationCircleOutline />
            </Box>
          </Tooltip>
        </Stack>
      </Box>
    </Paper>
  );
}

const TooltipComponent = ({ perMinuteCost }: { perMinuteCost?: number | string }) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Typography fontSize={12} fontWeight={600}>
        Minutes Usage Info
      </Typography>
      <Typography fontSize={12}>• Overuse Rate: {perMinuteCost}/ Minute</Typography>
    </Box>
  );
};
